import React from "react"
import Modal from "@react/shared/ui/Modal"

const CHIP = "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap"
const M    = `${CHIP} bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200`
const V    = `${CHIP} bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200`

// Built on render, not at load: I18n must know the locale first.
const rows = () => [
  [I18n.t("projects.profitability_help.rows.question.label"), I18n.t("projects.profitability_help.rows.question.margins"), I18n.t("projects.profitability_help.rows.question.valorisation")],
  [I18n.t("projects.profitability_help.rows.source.label"), I18n.t("projects.profitability_help.rows.source.margins"), I18n.t("projects.profitability_help.rows.source.valorisation")],
  [I18n.t("projects.profitability_help.rows.client_invoices.label"), I18n.t("projects.profitability_help.rows.client_invoices.margins"), I18n.t("projects.profitability_help.rows.client_invoices.valorisation")],
  [I18n.t("projects.profitability_help.rows.key_figure.label"), I18n.t("projects.profitability_help.rows.key_figure.margins"), I18n.t("projects.profitability_help.rows.key_figure.valorisation")],
  [I18n.t("projects.profitability_help.rows.who_when.label"), I18n.t("projects.profitability_help.rows.who_when.margins"), I18n.t("projects.profitability_help.rows.who_when.valorisation")],
]

const Stance = ({ tone, label, question, gloss }) => (
  <div className={`rounded-lg border border-slate-200 p-3.5 flex flex-col gap-2 border-t-[3px] ${tone === "m" ? "border-t-blue-600" : "border-t-violet-600"}`}>
    <span className={tone === "m" ? M : V}>{label}</span>
    <p className="text-[14px] font-semibold text-slate-900 leading-snug m-0">{question}</p>
    <p className="text-[12.5px] text-slate-500 m-0">{gloss}</p>
  </div>
)

const Formula = ({ tone, label, lines }) => (
  <div className="rounded-lg border border-slate-200 p-3.5 flex flex-col gap-2">
    <span className={tone === "m" ? M : V}>{label}</span>
    <p className="font-mono text-[11.5px] leading-relaxed text-slate-600 m-0">
      {lines.map(line => <span key={line} className="block">{line}</span>)}
    </p>
  </div>
)

const ProfitabilityHelpModal = ({ isOpen, onClose }) => (
  <Modal
    isOpen  = {isOpen}
    onClose = {onClose}
    size    = "lg"
    eyebrow = {I18n.t("projects.profitability_help.eyebrow")}
    title   = {I18n.t("projects.profitability_help.title")}
  >
    <div className="flex flex-col gap-5">
      <p className="text-[13.5px] text-slate-600 m-0">
        {I18n.t("projects.profitability_help.intro")}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Stance
          tone     = "m"
          label    = {I18n.t("projects.profitability_help.margins")}
          question = {I18n.t("projects.profitability_help.stance.margins.question")}
          gloss    = {I18n.t("projects.profitability_help.stance.margins.gloss")}
        />
        <Stance
          tone     = "v"
          label    = {I18n.t("projects.profitability_help.valorisation")}
          question = {I18n.t("projects.profitability_help.stance.valorisation.question")}
          gloss    = {I18n.t("projects.profitability_help.stance.valorisation.gloss")}
        />
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px] min-w-[520px]">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="px-3 py-2 font-semibold text-[10.5px] uppercase tracking-wider text-slate-400 w-[24%]">{I18n.t("projects.profitability_help.criterion")}</th>
                <th className="px-3 py-2"><span className={M}>{I18n.t("projects.profitability_help.margins")}</span></th>
                <th className="px-3 py-2"><span className={V}>{I18n.t("projects.profitability_help.valorisation")}</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows().map(([label, marges, valo]) => (
                <tr key={label} className="align-top">
                  <th className="px-3 py-2.5 text-left font-semibold text-slate-500">{label}</th>
                  <td className="px-3 py-2.5 text-slate-700 border-l border-slate-100">{marges}</td>
                  <td className="px-3 py-2.5 text-slate-700 border-l border-slate-100">{valo}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Formula
          tone  = "m"
          label = {I18n.t("projects.profitability_help.margins")}
          lines = {[I18n.t("projects.profitability_help.formulas.margin"), I18n.t("projects.profitability_help.formulas.study_variance"), I18n.t("projects.profitability_help.formulas.execution_variance")]}
        />
        <Formula
          tone  = "v"
          label = {I18n.t("projects.profitability_help.valorisation")}
          lines = {[I18n.t("projects.profitability_help.formulas.coef"), I18n.t("projects.profitability_help.formulas.earned"), I18n.t("projects.profitability_help.formulas.billing_variance")]}
        />
      </div>

      <div>
        <h4 className="text-[13px] font-semibold text-slate-900 m-0 mb-2">{I18n.t("projects.profitability_help.sign.title")}</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3.5">
            <p className="text-[13px] font-semibold text-emerald-800 m-0">{I18n.t("projects.profitability_help.sign.under_title")}</p>
            <p className="text-[12.5px] text-emerald-900/80 m-0 mt-1">
              {I18n.t("projects.profitability_help.sign.under_body")}
            </p>
          </div>
          <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-3.5">
            <p className="text-[13px] font-semibold text-rose-800 m-0">{I18n.t("projects.profitability_help.sign.over_title")}</p>
            <p className="text-[12.5px] text-rose-900/80 m-0 mt-1">
              {I18n.t("projects.profitability_help.sign.over_body")}
            </p>
          </div>
        </div>
      </div>

      <p className="text-[12.5px] text-slate-500 border-t border-slate-100 pt-3 m-0">
        {I18n.t("projects.profitability_help.footnote")}
      </p>
    </div>
  </Modal>
)

export default ProfitabilityHelpModal
